import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import { ColorPicker } from "antd";
import InputField from "../../../../CommonComponents/InputField"; // Import InputField
import CustomDropDown from "../../../../CommonComponents/CustomDropDown"; // Import CustomDropDown

export default function BasicProps({ theme, initialProps, onUpdate, syncKey, widgetType }) {
  const [widgetName, setWidgetName] = useState(initialProps?.widgetName ?? "");
  const [fontSize, setFontSize] = useState(initialProps?.fontSize ?? 16);
  const [keyFontSize, setKeyFontSize] = useState(initialProps?.keyFontSize ?? initialProps?.fontSize ?? 13);
  const [fontType, setFontType] = useState(initialProps?.fontType ?? "Roboto");
  const [textColor, setTextColor] = useState(initialProps?.textColor ?? "#000000");
  const [backgroundColor, setBackgroundColor] = useState(initialProps?.backgroundColor ?? "");
  const [hasCustomBackground, setHasCustomBackground] = useState(initialProps?.hasCustomBackground ?? false);
  const [borderColor, setBorderColor] = useState(initialProps?.borderColor ?? "#000000");
  const [borderSize, setBorderSize] = useState(initialProps?.borderSize ?? 1);
  const [borderRadius, setBorderRadius] = useState(initialProps?.borderRadius ?? 4);
  const [valueFontSize, setValueFontSize] = useState(initialProps?.valueFontSize ?? 40);

  const fontTypeList = [
    "Roboto",
    "Arial",
    "Helvetica"
  ];
  // Sync internal state only when switching widget/context (prevents typing flicker on frequent parent re-renders)
  useEffect(() => {
    if (!initialProps) return;
    setWidgetName(initialProps.widgetName ?? "");
    setFontSize(initialProps.fontSize ?? 16);
    setKeyFontSize(initialProps.keyFontSize ?? initialProps.fontSize ?? 13);
    setFontType(initialProps.fontType ?? "Roboto");
    setTextColor(initialProps.textColor ?? "#000000");
    setBackgroundColor(initialProps.backgroundColor ?? "");
    setHasCustomBackground(initialProps.hasCustomBackground ?? false);
    setBorderColor(initialProps.borderColor ?? "#000000");
    setBorderSize(initialProps.borderSize ?? 1);
    setBorderRadius(initialProps.borderRadius ?? 4);
    setValueFontSize(initialProps.valueFontSize ?? 40);
  }, [syncKey]);

  // Propagate changes upward for live preview
  useEffect(() => {
    if (typeof onUpdate === 'function') {
      onUpdate({
        widgetName,
        fontSize: Number(fontSize),
        keyFontSize: Number(keyFontSize),
        fontType,
        textColor,
        backgroundColor,
        hasCustomBackground,
        borderColor,
        borderSize: Number(borderSize),
        borderRadius: Number(borderRadius),
        valueFontSize: Number(valueFontSize),
      });
    }
  }, [widgetName, fontSize, keyFontSize, fontType, textColor, backgroundColor, borderColor, borderSize, borderRadius, valueFontSize, onUpdate]);

  return (
    <div style={{ 
      padding: "10px",
      color: theme==='light' ? '#7E7E7E' : '#FFFFFF',
        background: theme === "light" ? "#FFFFFF" : "#2C2C2C",
      }}>
      <InputField
        key="widgetName"
        label="Widget Name"
        required={true}
        value={widgetName}
        onChange={(e) => setWidgetName(e.target.value)}
        error={false}
        helperText=""
        lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
        focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
        FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
        style={{ width: "100%", color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', fontWeight: 400, fontSize: 16, borderRadius: 4 }}
        inputProps={{ readOnly: false }}
      />

    <Typography style={{ marginTop: "20px", marginBottom: "10px", fontSize: 18, fontWeight: 400, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Font Settings</Typography>
        <Box style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
          <InputField
            key="fontSize"
            label="Font Size"
            required={true}
            type="number"
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            error={false}
            helperText=""
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{ width: "250px", color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', fontWeight: 400, fontSize: 16, borderRadius: 4 }}
            inputProps={{ readOnly: false }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography style={{ fontSize: '18px', fontWeight: 400, color: "var(--GreyVariation_2)", width: "224px" }}>
              Font Type<span style={{ color: "var(--RedBaseColor)", marginLeft: "5px" }}>*</span>
            </Typography>
            <CustomDropDown
              options={fontTypeList}
              onChange={(value) => setFontType(value)}
              defaultValue={fontType}
              width="250px"
              fontSize={14}
              disabledOptions={[]}
              style={{ width: '250px', borderBottom: `1px solid ${theme === 'light' ? '#C7CED3' : '#FFFFFF'}` }}
            />
          </Box>
        </Box>

        {/* Color Settings */}
    <Typography style={{ marginTop: "20px", marginBottom: "10px", fontSize: 18, fontWeight: 400, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Color Settings</Typography>
        <Box style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
          <Box style={{ flex: 1 }}>
        <Typography style={{ fontWeight: 400, fontSize: 16, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Text Color</Typography>
            <ColorPicker
              value={textColor}
              onChangeComplete={(value) => setTextColor(value.toHexString())}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              style={{ zIndex: 9999 }}
            />
          </Box>
          <Box style={{ flex: 1 }}>
        <Typography style={{ fontWeight: 400, fontSize: 16, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Background Color</Typography>
            <ColorPicker
              value={backgroundColor || (theme === 'dark' ? '#1E1E1E' : '#FFFFFF')}
              onChangeComplete={(value) => {
                setBackgroundColor(value.toHexString());
                setHasCustomBackground(true);
              }}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              style={{ zIndex: 9999 }}
            />
            {hasCustomBackground && (
              <Typography
                onClick={() => { setBackgroundColor(''); setHasCustomBackground(false); }}
                style={{ fontSize: 11, color: '#008CD7', cursor: 'pointer', marginTop: 2 }}
              >
                Reset to theme default
              </Typography>
            )}
          </Box>
          <Box style={{ flex: 1 }}>
        <Typography style={{ fontWeight: 400, fontSize: 16, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Border Color</Typography>
            <ColorPicker
              value={borderColor}
              onChangeComplete={(value) => setBorderColor(value.toHexString())}
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              style={{ zIndex: 9999 }}
            />
          </Box>
        </Box>

        {/* Value Font Size (Value-like cards) */}
        {(
          // widgetType === "Single Value" ||
          // widgetType === "Multi Value" ||
          widgetType === "Value Card" ||
          widgetType === "Scheduler Status" ||
          widgetType === "Tag Value Card"
        ) && (
          <>
            <Typography style={{ marginTop: "20px", marginBottom: "10px", fontSize: 18, fontWeight: 400, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Value Display Settings</Typography>
            <Box style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
              <InputField
                key="keyFontSize"
                label="Key Font Size"
                required={false}
                type="number"
                value={keyFontSize}
                onChange={(e) => setKeyFontSize(Math.max(10, Math.min(72, Number(e.target.value) || 13)))}
                error={false}
                helperText=""
                lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
                focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
                FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
                style={{ width: "250px", color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', fontWeight: 400, fontSize: 16, borderRadius: 4 }}
                inputProps={{ readOnly: false, min: 10, max: 72 }}
              />
              <InputField
                key="valueFontSize"
                label="Value Font Size"
                required={false}
                type="number"
                value={valueFontSize}
                onChange={(e) => setValueFontSize(Math.max(10, Math.min(200, Number(e.target.value) || 40)))}
                error={false}
                helperText=""
                lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
                focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
                FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
                style={{ width: "250px", color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', fontWeight: 400, fontSize: 16, borderRadius: 4 }}
                inputProps={{ readOnly: false, min: 10, max: 200 }}
              />
            </Box>
          </>
        )}

        {/* Border Settings */}
    <Typography style={{ marginTop: "20px", marginBottom: "10px", fontSize: 18, fontWeight: 400, color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', borderRadius: 4 }}>Border Settings</Typography>
        <Box style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
          <InputField
            key="borderSize"
            label="Border Size"
            required={true}
            type="number"
            value={borderSize}
            onChange={(e) => setBorderSize(Number(e.target.value))}
            error={false}
            helperText=""
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{ width: "150px", color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', fontWeight: 400, fontSize: 16, borderRadius: 4 }}
            inputProps={{ readOnly: false }}
          />
          <InputField
            key="borderRadius"
            label="Border Radius"
            required={true}
            type="number"
            value={borderRadius}
            onChange={(e) => setBorderRadius(Number(e.target.value))}
            error={false}
            helperText=""
            lineColor={theme === "light" ? "#C7CED3" : "#FFFFFF"}
            focusLineColor={theme === "light" ? "#008CD7" : "#64B5F6"}
            FormHelperTextProps={{ sx: { maxWidth: "500px" } }}
            style={{ width: "150px", color: theme === 'light' ? '#7E7E7E' : '#FFFFFF', fontWeight: 400, fontSize: 16, borderRadius: 4 }}
            inputProps={{ readOnly: false }}
          />
        </Box>
    </div>
  );
}